import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { TextField, ToggleButton, ToggleButtonGroup, Slider, Box, Typography } from '@mui/material';
import { solarData } from './SolarData';

// Styled components
/* Sized for the customize panel, which is a quiet column of controls rather
   than the tall centred card this used to sit in. */
const ComponentWrapper = styled.div`
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const TopContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 44px;
  margin-bottom: 6px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Value = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f1b24;
  font-variant-numeric: tabular-nums;
`;

const Title = styled.div`
  font-size: 0.6rem;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  font-weight: 800;
  color: #8098a8;
  margin-top: 4px;
`;

const Form = styled.form`
  width: 100%;
  height: auto;
`;

/* The custom entry, restyled to sit inside the customize panel. It used to be a
   white-on-white outlined field — the border was literally set to white — beside
   default MUI toggle chrome, which looked nothing like the rest of the page. */
const StyledTextField = styled(TextField)`
  flex: 1;
  min-width: 110px;
  background: #fff;

  .MuiOutlinedInput-root {
    border-radius: 8px;
    font-size: 0.85rem;

    & fieldset {
      border-color: #e0e6ea;
    }
    &:hover fieldset {
      border-color: #cbd7df;
    }
    &.Mui-focused fieldset {
      border-color: #1ba8f1;
      border-width: 1px;
    }
  }

  .MuiOutlinedInput-input {
    color: #0f1b24;
    padding: 10px 12px;
  }
`;

const UnitToggle = styled(ToggleButtonGroup)`
  border: 1px solid #e0e6ea;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;

  .MuiToggleButton-root {
    border: 0;
    border-right: 1px solid #e0e6ea;
    border-radius: 0;
    text-transform: none;
    font-family: inherit;
    font-size: 0.8rem;
    color: #3c4e5a;
    padding: 9px 14px;

    &:last-of-type {
      border-right: 0;
    }

    &.Mui-selected {
      background-color: #1ba8f1;
      color: #04222f;
      font-weight: 800;

      &:hover {
        background-color: #1ba8f1;
      }
    }
  }
`;

const SetButton = styled.button`
  appearance: none;
  border: 0;
  border-radius: 8px;
  background: #1ba8f1;
  color: #04222f;
  font-family: inherit;
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 800;
  padding: 11px 18px;
  cursor: pointer;

  &:hover {
    background: #0b7fbd;
  }
`




const SliderContainer = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 18px 0 0;
`;

/* Was imported from Design.js, which made Design → CustomizeSection →
   OffsetSlider → Design a cycle. One line of shared styling is not worth a
   circular import. */
const MaxMessage = styled(Typography)`
  font-size: 0.75rem !important;
  color: #b06a00;
  margin: 10px 0 0 !important;
`;

const SliderHeader = styled(Typography)`
  font-size: 0.6rem !important;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-weight: 800 !important;
  color: #8098a8;
  margin-bottom: 4px !important;
`;

const CustomSlider = styled(Slider)`
  /* Mark labels are centred on their mark, so the ones at each end of the track
     hang half their width outside it — and the widest of them is "Custom", on
     the right-hand end. At full width that put it off the side of the card on a
     phone. Inset the track by roughly half a label so both ends land inside.
     Doubled selector because MUI's own root rule sets width:100% on the slider
     and wins against a single styled-components class; the margin alone moved
     the track's start without ever shortening it. */
  && {
    width: calc(100% - 52px);
    margin: 10px 26px 0;
  }

  color: #1ba8f1;

  .MuiSlider-mark {
    background-color: #fff;
    width: 14px;
    height: 14px;
    border: 3px solid #1ba8f1;
    border-radius: 50%;
    transform: translateX(-50%) translateY(-50%);
  }

  .MuiSlider-markActive {
    background-color: #1ba8f1;
    opacity: 1;
  }

  .MuiSlider-thumb {
    background-color: #1ba8f1;
    width: 18px;
    height: 18px;
  }

  .MuiSlider-rail {
    opacity: 0.28;
  }

  .MuiSlider-markLabel {
    font-size: 0.72rem;
    color: #8098a8;
  }
`;

const CustomInputContainer = styled.div`
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #e0e6ea;
  border-radius: 10px;
  background: #f6fafd;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SolarProductionOffsetSlider = ({ estimateSize, hardware, updateHardware, solarProductionMonthly }) => {
  const [customSelected, setCustomSelected] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [unit, setUnit] = useState('offset');
  const [selectedRadio, setSelectedRadio] = useState(1);
  const [calculatedValues, setCalculatedValues] = useState([]);
  const [maxMessage, setMaxMessage] = useState('');

    // Derived from backend values (naming kept as-is)
  const annualLoadKwh = useMemo(
    () => solarProductionMonthly * 12,
    [solarProductionMonthly]
  );

  const oversizeFactor = 1.105; // keep in sync with backend

  // Initial annual production for the estimated system (kWh/yr)
  const baseAnnualProdKwh = useMemo(
    () => annualLoadKwh * oversizeFactor,
    [annualLoadKwh]
  );

  // PVWatts yield per kW (kWh/yr per kW)
  const yieldPerKw = useMemo(
    () => annualLoadKwh / estimateSize,
    [annualLoadKwh, estimateSize]
  );


  const { maxOffset, maxSystemSize } = useMemo(() => {
    const annualProduction = solarProductionMonthly * 12;
    const maxProduction = 30000;
    const maxSystemSize = Math.floor((estimateSize * maxProduction) / annualProduction);
    const maxOffset = (maxSystemSize / estimateSize) * 100;
    return { maxOffset, maxSystemSize };
  }, [solarProductionMonthly, estimateSize]);

  const fixedOffsetValues = useMemo(() => {
    let values;
    if (maxOffset >= 115) {
      values = [90, 100, 115];
    } else {
      const maxOffsetRounded = Math.floor(maxOffset);
      const thirdOffset = maxOffsetRounded;
      const secondOffset = Math.max(maxOffsetRounded - 5, 0);
      const firstOffset = Math.max(secondOffset - 5, 0);
      values = [firstOffset, secondOffset, thirdOffset];
    }
    return values;
  }, [maxOffset]);

  const calculateSystemSize = useCallback(
    (value, unit) => {
      const moduleOption = solarData.modules.options.find(
        (option) => option.name === hardware.module.name
      );
      const wattsPerModule = moduleOption ? moduleOption.watts : 0;

      // How much array the customer asked for, before it is rounded up to a
      // whole number of whatever panel they happen to have selected. Kept
      // because it is the only module-independent record of their choice: the
      // rounded size and the offset derived from it both shift when the panel
      // wattage changes, which made the package cards reprice every time one
      // was clicked.
      let targetWatts = unit === 'kW' ? value * 1000 : (value / 100) * estimateSize;

      let newSystemSize = Math.ceil(targetWatts / wattsPerModule) * wattsPerModule;

      // Ensure newSystemSize does not exceed maxSystemSize
      if (newSystemSize > maxSystemSize) {
        newSystemSize = maxSystemSize;
        targetWatts = maxSystemSize;
        setMaxMessage(`* Max System Size is ${(maxSystemSize / 1000).toFixed(2)} kW`)
      }

      const newOffset = (newSystemSize / estimateSize) * 100;

      const newSystemSizeKw = newSystemSize / 1000;
      const targetEnergyAnnual = newSystemSizeKw * yieldPerKw; // kWh/yr

      return { newSystemSize, newOffset, targetEnergyAnnual, targetWatts };
    },
    [estimateSize, hardware.module.name, maxSystemSize, yieldPerKw]
  );

  useEffect(() => {
    const newValues = fixedOffsetValues.map((offset) => calculateSystemSize(offset, 'offset'));
    // console.log("update sys size and offset");
    setCalculatedValues(newValues);
    const defaultValues = newValues[1];
    updateHardware('systemSize', defaultValues.newSystemSize);
    updateHardware('offset', defaultValues.newOffset);
    updateHardware('targetEnergyAnnual', defaultValues.targetEnergyAnnual);
    updateHardware('targetWatts', defaultValues.targetWatts);
  }, [calculateSystemSize, updateHardware, fixedOffsetValues]);

  const handleRadioChange = useCallback(
    (event, newValue) => {
      setSelectedRadio(newValue);

      if (newValue === 3) {
        setCustomSelected(true);
      } else {
        setCustomSelected(false);
        setCustomValue('');

        const { newSystemSize, newOffset, targetEnergyAnnual, targetWatts } = calculateSystemSize(
          fixedOffsetValues[newValue],
          'offset'
        );
        updateHardware('systemSize', newSystemSize);
        updateHardware('offset', newOffset);
        updateHardware('targetEnergyAnnual', targetEnergyAnnual);
        updateHardware('targetWatts', targetWatts);
      }
    },
    [calculateSystemSize, fixedOffsetValues, updateHardware]
  );

  const handleCustomChange = useCallback((e) => {
    setCustomValue(e.target.value);
  }, []);

  const handleUnitChange = useCallback((event, newUnit) => {
    if (newUnit !== null) {
      setUnit(newUnit);
    }
  }, []);

  const handleConfirmCustomValue = useCallback(() => {
    let inputValue = parseFloat(customValue);

    if (isNaN(inputValue) || inputValue <= 0) {
      // Handle invalid input
      return;
    }

    if (unit === 'offset') {
      if (inputValue > maxOffset) {
        inputValue = maxOffset;
      }
    } else if (unit === 'kW') {
      const inputSystemSize = inputValue * 1000;
      if (inputSystemSize > maxSystemSize) {
        inputValue = maxSystemSize / 1000; // Convert back to kW
      }
    }

    const { newSystemSize, newOffset, targetEnergyAnnual, targetWatts } = calculateSystemSize(inputValue, unit);
    updateHardware('systemSize', newSystemSize);
    updateHardware('offset', newOffset);
    updateHardware('targetEnergyAnnual', targetEnergyAnnual);
    updateHardware('targetWatts', targetWatts);
  }, [calculateSystemSize, customValue, unit, updateHardware, maxOffset, maxSystemSize]);

  const marks = useMemo(
    () => [
      ...calculatedValues.map((value, index) => ({
        value: index,
        label: `${value.newOffset.toFixed(0)}%`,
      })),
      { value: 3, label: 'Custom' },
    ],
    [calculatedValues]
  );

  function valuetext(value) {
    return `${value}`;
  }

  const valueLabelFormat = useCallback(
    (value) => {
      const mark = marks.find((mark) => mark.value === value);
      return mark ? mark.label : value;
    },
    [marks]
  );

  return (
    <ComponentWrapper>
      <TopContainer>
        <Column>
          <Value>{(Math.round(hardware.systemSize) / 1000).toFixed(2)} kW</Value>
          <Title>System Size</Title>
        </Column>
        <Column>
          <Value>{Math.round(hardware.offset)}%</Value>
          <Title>Offset</Title>
        </Column>
      </TopContainer>
      <Form>
        <SliderContainer>
          <SliderHeader align="left" variant="body1">
            Choose Your Energy Offset %
          </SliderHeader>
          <CustomSlider
            value={selectedRadio}
            onChange={handleRadioChange}
            aria-labelledby="offset-slider"
            getAriaValueText={valuetext}
            valueLabelDisplay="auto"
            valueLabelFormat={valueLabelFormat}
            step={1}
            marks={marks}
            min={0}
            max={3}
          />
        </SliderContainer>
      </Form>
      {customSelected && (
        <>
        <MaxMessage>{maxMessage}</MaxMessage>
        <CustomInputContainer>
          <FlexRow>
            <StyledTextField
              placeholder={unit === 'kW' ? 'kW' : 'Offset'}
              variant="outlined"
              value={customValue}
              onChange={handleCustomChange}
            />
            <UnitToggle
              value={unit}
              exclusive
              onChange={handleUnitChange}
              aria-label="unit"
            >
              <ToggleButton value="kW" aria-label="kW">
                kW
              </ToggleButton>
              <ToggleButton value="offset" aria-label="offset">
                Offset
              </ToggleButton>
            </UnitToggle>
            <SetButton type="button" onClick={handleConfirmCustomValue}>
              Set
            </SetButton>
          </FlexRow>
        </CustomInputContainer>
        </>
      )}
    </ComponentWrapper>
  );
};

export default SolarProductionOffsetSlider;

